import type { IUser } from '@featit/shared';
import { usePhoneNavigation } from '@/shell/usePhoneNavigation';

type AvatarSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg';

interface AvatarProps {
  user?: IUser;
  name?: string;
  /**
   * F-0579 ACC-8: the avatar is an agent's. In Colour it wears the three strands, a ring in the frame
   * stops; a person's stays plain. Quiet draws both plain.
   */
  agent?: boolean;
  size?: AvatarSize;
  className?: string;
}

const sizeMap: Record<AvatarSize, number> = {
  // A name inside a line of small text (a Tasks for me plan action, I-1463): no taller than the line.
  xxs: 16,
  xs: 20,
  sm: 24,
  md: 32,
  lg: 40,
};

/*
 * The initials' size per circle. IU-4's 11 px floor for initials applied only in phone mode, where
 * an avatar draws nothing since AC-19.14.3 (I-1348), so the floor is gone with it.
 */
const textSizeMap: Record<AvatarSize, string> = {
  xxs: 'text-[7px]',
  xs: 'text-[8px]',
  sm: 'text-[9px]',
  md: 'text-xs',
  lg: 'text-sm',
};

/**
 * The fills an avatar without a photo is drawn on, white initials on each. I-0890: every one holds
 * white at 4.5:1 or more (`hueContrast.guard.test.ts`); the 500s it used held 2.2–4.5.
 */
export const AVATAR_FILLS = [
  // The darkest step of each hue that still reads as that hue, holding white at 4.6–6.3:1.
  'bg-blue-600',
  'bg-emerald-700',
  'bg-amber-700',
  'bg-rose-600',
  'bg-violet-600',
  'bg-cyan-700',
  'bg-pink-600',
  'bg-indigo-600',
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * AC-19.14.3 (I-1348; Jay, 2026-10-09: "generally dont show avatar icons on mobile. they are not
 * useful identifiers usually"): at phone width an avatar draws nothing, wherever it is used. A list
 * row names the person in its subtitle instead (`PhoneRowResponsible`); a desktop and an iPad keep
 * their circles.
 */
export function Avatar({ user, name, agent = false, size = 'md', className = '' }: AvatarProps) {
  const phone = usePhoneNavigation();
  if (phone) return null;

  const displayName = user?.name ?? name ?? '?';
  const avatarUrl = user?.avatarUrl;
  const px = sizeMap[size];

  if (avatarUrl) {
    return (
      <img
        data-avatar=""
        data-accent-agent={agent ? '' : undefined}
        src={avatarUrl}
        alt={displayName}
        className={`rounded-full object-cover shrink-0 ${className}`}
        style={{ width: px, height: px }}
      />
    );
  }

  const colorClass = AVATAR_FILLS[hashString(displayName) % AVATAR_FILLS.length];

  return (
    // The circle keeps its size at every text size, and its initials stay inside it (T-0366).
    <div
      data-avatar=""
      data-accent-agent={agent ? '' : undefined}
      className={`rounded-full flex items-center justify-center overflow-hidden text-white font-medium shrink-0 ${colorClass} ${textSizeMap[size]} ${className}`}
      style={{ width: px, height: px }}
      title={displayName}
    >
      {getInitials(displayName)}
    </div>
  );
}
